import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MessageSquare,
  Send,
  Users,
  Clock,
  CheckCheck,
  Check,
  User,
  Search,
  Phone,
  Video,
  MoreVertical,
  ArrowLeft,
} from "lucide-react";
import "./MessagingEnhanced.css";

const Messaging = ({
  currentUserId,
  currentUserType = "student",
  currentUserName = "User",
  onMessageCount = null,
}) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

  const getAuthToken = () => {
    return (
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Setup WebSocket connection
  const setupWebSocket = useCallback(() => {
    if (!currentUserId) return;

    // Convert HTTP URL to WebSocket URL
    const apiUrl = API_BASE_URL;
    const wsUrl =
      apiUrl.replace(/^http/, "ws") +
      `/api/messaging/ws/${currentUserId}?token=${encodeURIComponent(
        localStorage.getItem("access_token") || sessionStorage.getItem("access_token") || ""
      )}`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log("WebSocket connected");
      setIsConnected(true);
    };

    wsRef.current.onmessage = (event) => {
      const messageData = JSON.parse(event.data);
      console.log("Received message:", messageData);

      // Add new message to the current conversation if it's active
      if (
        activeConversation &&
        messageData.sender_id ===
          activeConversation.participants.find((p) => p !== currentUserId)
      ) {
        setMessages((prev) => [messageData, ...prev]);
      }

      // Update conversations list
      fetchConversations();
      fetchUnreadCount();
    };

    wsRef.current.onclose = () => {
      console.log("WebSocket disconnected");
      setIsConnected(false);
      // Attempt to reconnect after 3 seconds
      setTimeout(() => {
        if (currentUserId) setupWebSocket();
      }, 3000);
    };

    wsRef.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setIsConnected(false);
    };

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [currentUserId, activeConversation]);

  // API call helper
  const apiCall = async (endpoint, options = {}) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    return await response.json();
  };

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!currentUserId) return;

    try {
      const data = await apiCall(
        `/api/messaging/conversations/${currentUserId}`
      );
      setConversations(data);
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError("Failed to load conversations");
    }
  }, [currentUserId]);

  // Fetch messages for a conversation
  const fetchMessages = async (conversationId) => {
    try {
      setLoading(true);
      const data = await apiCall(
        `/api/messaging/messages/conversation/${conversationId}`
      );
      setMessages(data.reverse()); // Reverse to show oldest first
      scrollToBottom();
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for new conversation (teachers for students)
  const fetchUsers = useCallback(async (query = "") => {
    try {
      setLoading(true);
      // For students, fetch teachers they can message
      const searchParams = query ? `?query=${encodeURIComponent(query)}` : "";
      const response = await apiCall(`/profile/teachers/search${searchParams}`);
      if (response.success) {
        setUsers(response.teachers);
      }
    } catch (err) {
      console.error("Error fetching teachers:", err);
      setError("Failed to load teachers");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread message count
  const fetchUnreadCount = useCallback(async () => {
    if (!currentUserId) return;

    try {
      const data = await apiCall(
        `/api/messaging/messages/unread/count/${currentUserId}`
      );
      setUnreadCount(data.unread_count);
      if (onMessageCount) {
        onMessageCount(data.unread_count);
      }
    } catch (err) {
      console.error("Error fetching unread count:", err);
    }
  }, [currentUserId, onMessageCount]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    const otherParticipant = activeConversation.participants.find(
      (p) => p !== currentUserId
    );
    const otherParticipantType =
      activeConversation.participant_types[otherParticipant];

    try {
      const messageData = {
        sender_id: currentUserId,
        sender_type: currentUserType,
        receiver_id: otherParticipant,
        receiver_type: otherParticipantType,
        content: newMessage,
        message_type: "text",
      };

      const sentMessage = await apiCall("/api/messaging/messages/", {
        method: "POST",
        body: JSON.stringify(messageData),
      });

      // Add message to current conversation
      setMessages((prev) => [...prev, sentMessage]);
      setNewMessage("");
      scrollToBottom();

      // Refresh conversations to update last message
      fetchConversations();
    } catch (err) {
      console.error("Error sending message:", err);
      setError("Failed to send message");
    }
  };

  // Mark conversation as read
  const markConversationRead = async (conversationId) => {
    try {
      await apiCall(`/api/messaging/conversations/${conversationId}/read`, {
        method: "PUT",
      });
      fetchUnreadCount();
      fetchConversations();
    } catch (err) {
      console.error("Error marking conversation as read:", err);
    }
  };

  // Select conversation
  const selectConversation = (conversation) => {
    setActiveConversation(conversation);
    fetchMessages(conversation.id);
    markConversationRead(conversation.id);
  };

  // Start new conversation
  const startNewConversation = async (selectedUser) => {
    try {
      const conversationData = {
        participants: [currentUserId, selectedUser.user_id],
        participant_names: {
          [currentUserId]: currentUserName,
          [selectedUser.user_id]: `${selectedUser.name} ${
            selectedUser.surname || ""
          }`.trim(),
        },
        participant_types: {
          [currentUserId]: currentUserType,
          [selectedUser.user_id]: selectedUser.role,
        },
      };

      const newConversation = await apiCall("/api/messaging/conversations/", {
        method: "POST",
        body: JSON.stringify(conversationData),
      });

      setConversations((prev) => [newConversation, ...prev]);
      setActiveConversation(newConversation);
      setShowNewConversation(false);
      setMessages([]);
    } catch (err) {
      console.error("Error creating conversation:", err);
      setError("Failed to start conversation");
    }
  };

  // Initialize component
  useEffect(() => {
    if (currentUserId) {
      setLoading(true);
      Promise.all([fetchConversations(), fetchUnreadCount()]).finally(() => {
        setLoading(false);
      });
    }
  }, [currentUserId, fetchConversations, fetchUnreadCount]);

  // Setup WebSocket
  useEffect(() => {
    const cleanup = setupWebSocket();
    return cleanup;
  }, [setupWebSocket]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Filter conversations based on search
  const filteredConversations = conversations.filter((conv) => {
    const otherParticipant = conv.participants.find((p) => p !== currentUserId);
    const name = conv.participant_names[otherParticipant] || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading && !conversations.length) {
    return (
      <div className="messaging-container">
        <div className="messaging-loading">
          <MessageSquare size={48} />
          <p>Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`messaging-container${activeConversation ? " has-active" : ""}`}>
      {/* Sidebar - Conversations List */}
      <div className="conversations-sidebar">
        <div className="conversations-header">
          <h3>
            <MessageSquare size={20} />
            Messages{" "}
            {unreadCount > 0 && (
              <span className="unread-badge">{unreadCount}</span>
            )}
          </h3>
          <button
            className="new-conversation-btn"
            onClick={() => {
              setShowNewConversation(true);
              setUserSearchTerm("");
              fetchUsers();
            }}
          >
            <Users size={16} />
          </button>
        </div>

        <div className="search-container">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="conversations-list">
          {filteredConversations.length === 0 ? (
            <div className="no-conversations">
              <MessageSquare size={32} />
              <p>No conversations yet</p>
              <small>
                Start a new conversation with a{" "}
                {currentUserType === "student" ? "teacher" : "student"}
              </small>
            </div>
          ) : (
            filteredConversations.map((conversation) => {
              const otherParticipant = conversation.participants.find(
                (p) => p !== currentUserId
              );
              const name =
                conversation.participant_names[otherParticipant] ||
                "Unknown User";
              const userType = conversation.participant_types[otherParticipant];

              return (
                <div
                  key={conversation.id}
                  className={`conversation-item ${
                    activeConversation?.id === conversation.id ? "active" : ""
                  }`}
                  onClick={() => selectConversation(conversation)}
                >
                  <div className="conversation-avatar">
                    <User size={20} />
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-name">{name}</div>
                    <div className="conversation-type">{userType}</div>
                    {conversation.last_message && (
                      <div className="conversation-preview">
                        {conversation.last_message}
                      </div>
                    )}
                  </div>
                  <div className="conversation-meta">
                    {conversation.last_message_time && (
                      <div className="conversation-time">
                        {new Date(
                          conversation.last_message_time
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                    {conversation.unread_count > 0 && (
                      <div className="conversation-unread">
                        {conversation.unread_count}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="chat-area">
        {activeConversation ? (
          <>
            {/* Chat Header */}
            <div className="chat-header">
              <button
                type="button"
                className="chat-back-btn"
                onClick={() => setActiveConversation(null)}
                aria-label="Back to conversations"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="chat-user-info">
                <User size={24} />
                <div>
                  <div className="chat-user-name">
                    {
                      activeConversation.participant_names[
                        activeConversation.participants.find(
                          (p) => p !== currentUserId
                        )
                      ]
                    }
                  </div>
                  <div className="chat-user-type">
                    {
                      activeConversation.participant_types[
                        activeConversation.participants.find(
                          (p) => p !== currentUserId
                        )
                      ]
                    }
                  </div>
                </div>
              </div>
              <div className="chat-actions">
                <div
                  className={`connection-status ${
                    isConnected ? "connected" : "disconnected"
                  }`}
                >
                  {isConnected ? "Online" : "Offline"}
                </div>
                <button className="chat-action-btn">
                  <Phone size={16} />
                </button>
                <button className="chat-action-btn">
                  <Video size={16} />
                </button>
                <button className="chat-action-btn">
                  <MoreVertical size={16} />
                </button>
              </div>
            </div>

            {/* Messages List */}
            <div className="messages-list">
              {messages.length === 0 ? (
                <div className="no-messages">
                  <MessageSquare size={32} />
                  <p>No messages yet</p>
                  <small>Start the conversation!</small>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`message ${
                      message.sender_id === currentUserId ? "sent" : "received"
                    }`}
                  >
                    <div className="message-content">
                      <p>{message.content}</p>
                      <div className="message-meta">
                        <span className="message-time">
                          {new Date(message.timestamp).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        {message.sender_id === currentUserId && (
                          <span className="message-status">
                            {message.read ? (
                              <CheckCheck size={14} />
                            ) : (
                              <Check size={14} />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="message-input">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                disabled={!isConnected}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || !isConnected}
              >
                <Send size={16} />
              </button>
            </form>
          </>
        ) : (
          <div className="no-active-conversation">
            <MessageSquare size={48} />
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the sidebar to start messaging</p>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Start New Conversation</h3>
              <button onClick={() => setShowNewConversation(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="search-container modal-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search teachers by name or subject..."
                  value={userSearchTerm}
                  onChange={(e) => {
                    setUserSearchTerm(e.target.value);
                    fetchUsers(e.target.value);
                  }}
                />
              </div>
              <div className="users-list">
                {users.map((user) => (
                  <div
                    key={user.user_id}
                    className="user-item"
                    onClick={() => startNewConversation(user)}
                  >
                    <User size={20} />
                    <div className="user-details">
                      <div className="user-name">
                        {user.display_name ||
                          `${user.name} ${user.surname || ""}`.trim()}
                      </div>
                      <div className="user-info">
                        {user.subject && (
                          <span className="user-subject">{user.subject}</span>
                        )}
                        {user.school && (
                          <span className="user-school">{user.school}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default Messaging;
